'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes", "sx"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { UnreadMarker } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { useChatUnreadMarkerUtilityClasses } from "./chatUnreadMarkerClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatUnreadMarker');
const ChatUnreadMarkerStyled = styled('div', {
  name: 'MuiChatUnreadMarker',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(0.5),
  // Flex-filled rules on either side of the centered chip.
  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: 1,
    backgroundColor: (theme.vars || theme).palette.divider
  }
}));
const ChatUnreadMarkerLabelStyled = styled('span', {
  name: 'MuiChatUnreadMarker',
  slot: 'Label',
  overridesResolver: (_, styles) => styles.label
})(({
  theme
}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  maxWidth: '100%',
  paddingInline: theme.spacing(1),
  borderRadius: 12,
  backgroundColor: (theme.vars || theme).palette.action.selected,
  color: (theme.vars || theme).palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  flexShrink: 0
}));
const ChatUnreadMarker = /*#__PURE__*/React.forwardRef(function ChatUnreadMarker(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatUnreadMarker'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatUnreadMarkerUtilityClasses(classesProp);
  return /*#__PURE__*/_jsx(UnreadMarker, _extends({
    ref: ref
  }, other, {
    slots: _extends({}, slots, {
      root: slots?.root ?? ChatUnreadMarkerStyled,
      label: slots?.label ?? ChatUnreadMarkerLabelStyled
    }),
    slotProps: _extends({}, slotProps, {
      root: mergeSlotProps({
        className: clsx(classes.root, className),
        sx
      }, slotProps?.root),
      label: mergeSlotProps({
        className: classes.label
      }, slotProps?.label)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatUnreadMarker.displayName = "ChatUnreadMarker";
process.env.NODE_ENV !== "production" ? ChatUnreadMarker.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  index: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.node,
  messageId: PropTypes.string.isRequired,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatUnreadMarker };